import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { startTransition, useCallback } from 'react';
import { useShallow } from 'zustand/shallow';
import { useFilterEvents } from '../useFilterEvents';
import { MobileDialogStoreProps } from './types';

export type MobileDialogHeaderProps<TFilterState, TSortValue> = {
  handleOpenCloseDialog: () => void;
  useStore: MobileDialogStoreProps<TFilterState, TSortValue>;
  dialogId: string;
  title?: string;
  filterPillsToDisplay: number;
  actionSection: string;
  pageIdentifier: string;
  onLocationReset?: () => void;
};

export const MobileDialogHeader = <TFilterState, TSortValue>({
  handleOpenCloseDialog,
  useStore,
  dialogId,
  title = 'FILTERS & SORT',
  filterPillsToDisplay,
  actionSection,
  pageIdentifier,
  onLocationReset,
}: MobileDialogHeaderProps<TFilterState, TSortValue>) => {
  const { clearAllFiltersEvent } = useFilterEvents({
    actionSection,
    pageIdentifier,
  });
  const { handleClearFilters, sortBy } = useStore(
    useShallow(state => ({
      handleClearFilters: state.handleClearFilters,
      sortBy: state.sortBy,
    })),
  );

  const handleClearAllFilters = useCallback(() => {
    setTimeout(() => {
      startTransition(() => {
        handleClearFilters();

        if (onLocationReset) {
          onLocationReset();
        }

        clearAllFiltersEvent();
      });
    }, 100);
  }, [handleClearFilters, onLocationReset, clearAllFiltersEvent]);

  return (
    <Box
      sx={{
        px: 3,
        pt: '22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography id={dialogId} sx={{ fontSize: 16, fontWeight: 600, color: 'text.secondary' }}>
        {title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Chip
          label="Clear All"
          variant="filterPills"
          onClick={handleClearAllFilters}
          disabled={!filterPillsToDisplay && !sortBy}
        />
        <IconButton aria-label="close dialog" onClick={handleOpenCloseDialog} size="small">
          <CloseIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
        </IconButton>
      </Box>
    </Box>
  );
};
